import React from "react";
import { chipLabel, scoreHex, readinessLabel, MS_STATUS, PRIORITY_META } from "@/lib/careerBuilder";
import { translateFr as tfr } from "@/i18n";

import { dateLocale } from "@/lib/utils";
/* Live A4 preview mirroring the premium PDF (2 pages, light document theme). */

const fmtDate = (d) => (d && d.length >= 10 ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : "");

function Page({ children, num }) {
  return (
    <div className="bg-[#F4F5F7] text-[#111827] w-full aspect-[210/297] rounded-md shadow-2xl overflow-hidden flex flex-col relative"
      style={{ fontFamily: "Helvetica, Arial, sans-serif" }} data-testid={`career-a4-page-${num}`}>
      {children}
      <div className="absolute bottom-1.5 inset-x-4 flex justify-between text-[6px] text-[#9CA3AF]">
        <span>{tfr("Indicateurs d'aide à la décision — ne prédisent pas le succès sportif.")}</span>
        <span>ATHLYNX · Page {num}/2</span>
      </div>
    </div>
  );
}

function Header({ idn, compact }) {
  return (
    <div className={`bg-[#101318] px-4 flex items-center gap-2.5 ${compact ? "py-2" : "py-3"} border-b-[3px] border-[#E11D2E]`}>
      <div className="h-7 w-7 rounded bg-[#E11D2E] flex items-center justify-center text-white font-bold text-sm shrink-0">A</div>
      <div className="min-w-0 flex-1">
        {compact ? (
          <p className="text-white font-bold text-[9px] truncate">ATHLYNX — PLAN DE CARRIÈRE · {idn.name || "—"}</p>
        ) : (
          <>
            <p className="text-[#D1D5DB] font-bold text-[6.5px] tracking-widest">ATHLYNX</p>
            <p className="text-white font-bold text-[13px] leading-tight">{tfr("PLAN DE CARRIÈRE")}</p>
            <p className="text-[#E5E7EB] text-[7px] font-semibold truncate">
              {[idn.name, idn.position, idn.club, idn.league].filter(Boolean).join("  •  ") || "—"}
            </p>
          </>
        )}
      </div>
      <div className="text-right shrink-0">
        <p className="text-[#9CA3AF] text-[6px]">Document interne — <span className="text-[#E11D2E]">Confidentiel</span></p>
        <p className="text-white text-[6.5px]">{new Date().toLocaleDateString(dateLocale())}</p>
      </div>
    </div>
  );
}

const Card = ({ children, className = "", style }) => (
  <div className={`bg-white rounded-lg border border-[#E5E7EB] ${className}`} style={style}>{children}</div>
);
const SecTitle = ({ children, color = "#111827" }) => (
  <p className="text-[7px] font-bold uppercase tracking-wider" style={{ color }}>{children}</p>
);

export function CareerA4Preview({ draft, meta, catalogs }) {
  if (!draft) return null;
  const idn = draft.identity || {};
  const ms = (draft.milestones || []).filter((m) => m.title).slice(0, 6);
  const goals = (draft.goals || []).filter((g) => g.title).slice(0, 5);
  const dims = meta?.dims || {};
  const overall = meta?.overall;
  const velocity = meta?.velocity;
  const dimList = (catalogs?.readiness_dims || []).map((d) => ({ ...d, v: dims[d.id] }));
  const risks = draft.risks || [];
  const opps = draft.opportunities || [];
  const chips = (group, ids) => (ids || []).map((i) => chipLabel(catalogs, group, i));

  return (
    <>
      <Page num={1}>
        <Header idn={idn} />
        <div className="flex-1 min-h-0 p-2.5 grid grid-cols-[1.7fr_1fr] gap-2 items-start overflow-hidden">
          <div className="space-y-2 min-w-0">
            {/* Vision */}
            <Card className="p-2 flex gap-2 items-start" data-testid="preview-vision">
              <div className="h-6 w-6 rounded bg-[#101318] flex items-center justify-center shrink-0">
                <span className="h-2.5 w-2.5 rounded-full border-2 border-[#E11D2E]" />
              </div>
              <div className="min-w-0">
                <SecTitle>{tfr("Vision carrière")}</SecTitle>
                <p className="text-[7.5px] leading-snug mt-0.5 line-clamp-3">{draft.vision?.text || "—"}</p>
              </div>
            </Card>
            {/* Roadmap */}
            <Card className="p-2" data-testid="preview-roadmap">
              <SecTitle>Feuille de route ({draft.vision?.horizon_months || 36} mois)</SecTitle>
              <div className="mt-1.5 space-y-0">
                {ms.length === 0 && <p className="text-[7px] text-[#9CA3AF]">{tfr("Ajoutez des jalons à l'étape 3.")}</p>}
                {ms.map((m, i) => {
                  const st = MS_STATUS[m.status] || MS_STATUS.upcoming;
                  return (
                    <div key={m.id} className="flex items-center gap-1.5 relative py-[3px]">
                      {i < ms.length - 1 && <span className="absolute left-[3.5px] top-[13px] bottom-[-4px] w-px bg-[#E5E7EB]" />}
                      <span className="h-2 w-2 rounded-full shrink-0 z-[1]" style={{ background: st.hex }} />
                      <span className="text-[7.5px] font-bold w-7 shrink-0">M+{m.target_month ?? "?"}</span>
                      <span className="text-[7px] flex-1 truncate">{m.title}{m.is_key && <span className="text-[#F59E0B] font-bold"> ★</span>}</span>
                      <span className="text-[5.5px] font-bold px-1.5 py-0.5 rounded-full shrink-0"
                        style={{ color: st.hex, background: `${st.hex}1f`, border: `0.5px solid ${st.hex}55` }}>{st.label}</span>
                    </div>
                  );
                })}
              </div>
            </Card>
            {/* Objectives */}
            <Card className="p-2" data-testid="preview-objectives">
              <SecTitle>{tfr("Objectifs en cours")}</SecTitle>
              <div className="mt-1.5 space-y-1.5">
                {goals.length === 0 && <p className="text-[7px] text-[#9CA3AF]">{tfr("Ajoutez des objectifs à l'étape 4.")}</p>}
                {goals.map((g) => (
                  <div key={g.id} className="flex items-center gap-1.5">
                    <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: PRIORITY_META[g.priority]?.dot || "#F59E0B" }} />
                    <span className="text-[7px] flex-1 truncate">{g.title}</span>
                    <span className="h-[4px] w-14 rounded-full bg-[#E5E7EB] overflow-hidden shrink-0">
                      <span className="block h-full rounded-full bg-[#3B82F6]" style={{ width: `${g.progress || 0}%` }} />
                    </span>
                    <span className="text-[6.5px] font-bold w-6 text-right tabular-nums">{g.progress || 0}%</span>
                    <span className="text-[6px] text-[#6B7280] w-16 text-right truncate">
                      {[{ agent: "Agent", athlete: tfr("Athlète"), club: "Club", coach: "Coach" }[g.owner_type] || "Agent", fmtDate(g.due_date)].filter(Boolean).join(" · ")}
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
          <div className="space-y-2 min-w-0">
            {/* Readiness */}
            <Card className="p-2" data-testid="preview-readiness">
              <SecTitle>Career Readiness</SecTitle>
              <div className="flex flex-col items-center py-1.5">
                <div className="relative h-14 w-14 rounded-full grid place-items-center"
                  style={{ background: overall != null ? `conic-gradient(${scoreHex(overall)} ${overall * 3.6}deg, #E5E7EB 0deg)` : "#E5E7EB" }}>
                  <div className="h-10 w-10 rounded-full bg-white grid place-items-center">
                    <span className="text-[10px] font-bold">{overall != null ? `${overall}%` : "—"}</span>
                  </div>
                </div>
                <p className="text-[6px] font-bold mt-1" style={{ color: scoreHex(overall) }}>{readinessLabel(overall).toUpperCase()}</p>
              </div>
              <div className="space-y-[3px]">
                {dimList.map((d) => (
                  <div key={d.id} className="flex items-center gap-1">
                    <span className="text-[6px] text-[#6B7280] w-14 truncate">{d.label}</span>
                    <span className="h-[3px] flex-1 rounded-full bg-[#E5E7EB] overflow-hidden">
                      {d.v != null && <span className="block h-full rounded-full" style={{ width: `${d.v}%`, background: scoreHex(d.v) }} />}
                    </span>
                    <span className="text-[6px] font-bold w-6 text-right tabular-nums">{d.v != null ? `${d.v}%` : "—"}</span>
                  </div>
                ))}
              </div>
            </Card>
            {risks.length > 0 && (
              <Card className="p-2 !bg-[#FEF2F2] !border-[#E11D2E]/30" data-testid="preview-risks">
                <SecTitle color="#E11D2E">Points de vigilance</SecTitle>
                <div className="mt-1 space-y-1">
                  {risks.slice(0, 5).map((r) => (
                    <p key={r} className="text-[6.5px] flex items-center gap-1">
                      <span className="h-2.5 w-2.5 rounded-full bg-[#E11D2E] text-white text-[5px] font-bold grid place-items-center shrink-0">!</span>
                      <span className="truncate">{chipLabel(catalogs, "risks", r)}</span>
                    </p>
                  ))}
                </div>
              </Card>
            )}
            {opps.length > 0 && (
              <Card className="p-2 !bg-[#F0FDF4] !border-[#16A34A]/30" data-testid="preview-opps">
                <SecTitle color="#16A34A">{tfr("Opportunités")}</SecTitle>
                <div className="mt-1 space-y-1">
                  {opps.slice(0, 5).map((o) => (
                    <p key={o} className="text-[6.5px] flex items-center gap-1">
                      <span className="h-2.5 w-2.5 rounded-full bg-[#16A34A] text-white text-[5px] font-bold grid place-items-center shrink-0">✓</span>
                      <span className="truncate">{chipLabel(catalogs, "opportunities", o)}</span>
                    </p>
                  ))}
                </div>
              </Card>
            )}
            <Card className="p-2" data-testid="preview-velocity">
              <SecTitle>Career Velocity</SecTitle>
              <p className="mt-0.5">
                <span className="text-[13px] font-bold text-[#3B82F6]">{velocity ?? "—"}</span>
                <span className="text-[6.5px] text-[#6B7280]">/100</span>
              </p>
              {velocity != null && (
                <p className="text-[6px] font-bold" style={{ color: scoreHex(velocity) }}>
                  {velocity >= 70 ? tfr("Bonne dynamique") : velocity >= 40 ? tfr("Dynamique régulière") : tfr("Dynamique à relancer")}
                </p>
              )}
            </Card>
          </div>
        </div>
      </Page>

      <Page num={2}>
        <Header idn={idn} compact />
        <div className="flex-1 min-h-0 p-2.5 space-y-2 overflow-hidden">
          {[["Forces du profil", "strengths", draft.strengths], ["Style de jeu", "styles", draft.styles],
            [tfr("Environnement recherché"), "environments", draft.environments]].map(([title, group, ids]) =>
            (ids || []).length > 0 && (
              <Card key={group} className="p-2" data-testid={`preview-${group}`}>
                <SecTitle>{title}</SecTitle>
                <div className="flex flex-wrap gap-1 mt-1">
                  {chips(group, ids).map((lb) => (
                    <span key={lb} className="text-[6.5px] px-1.5 py-0.5 rounded-full bg-[#F3F4F6] border border-[#E5E7EB]">{lb}</span>
                  ))}
                </div>
              </Card>
            ))}
          {(draft.highlights || []).length > 0 && (
            <Card className="p-2" data-testid="preview-highlights">
              <SecTitle>Career highlights</SecTitle>
              <div className="mt-1 space-y-1">
                {draft.highlights.slice(0, 6).map((h) => (
                  <p key={h} className="text-[6.5px] flex items-center gap-1">
                    <span className="h-2.5 w-2.5 rounded-full bg-[#16A34A] text-white text-[5px] font-bold grid place-items-center shrink-0">✓</span>
                    <span className="truncate">{h}</span>
                  </p>
                ))}
              </div>
            </Card>
          )}
          <div className="bg-[#101318] rounded-lg p-2.5 flex gap-2 items-start" data-testid="preview-contact">
            <div className="flex-1 min-w-0">
              <SecTitle color="#fff">Contact agent</SecTitle>
              <div className="mt-1 space-y-[3px]">
                {[["Agent", draft.contact?.name], ["Agence", draft.contact?.agency], ["Email", draft.contact?.email],
                  [tfr("Téléphone"), draft.contact?.phone], ["Site", draft.contact?.website]].map(([lb, v]) => v && (
                  <p key={lb} className="text-[6.5px]"><span className="text-[#9CA3AF]">{lb}</span>{"  "}
                    <span className="text-white font-semibold">{v}</span></p>
                ))}
              </div>
            </div>
            <div className="h-12 w-12 rounded bg-white grid place-items-center shrink-0" aria-hidden>
              <div className="h-9 w-9 grid grid-cols-4 grid-rows-4 gap-[1px]">
                {[...Array(16)].map((_, i) => (
                  <span key={i} className={(i * 7 + 3) % 3 ? "bg-[#101318]" : "bg-white"} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </Page>
    </>
  );
}
